import { useEffect, useMemo, useState } from "react"
import type { EntryRow } from "../api/receipts"
import { suggestMapping } from "../api/receipts"
import { useApi } from "../api/hooks"
import type { CellMeta } from "../api/book"

interface Voce {
  key: string
  label: string
  byDay: Record<number, string> // day(1..7) → cell code
}

function weekMonday(weekKey: string): Date | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekKey)
  if (!m) return null
  const year = Number(m[1]), week = Number(m[2])
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const jan4Dow = (jan4.getUTCDay() + 6) % 7
  const mon = new Date(jan4)
  mon.setUTCDate(jan4.getUTCDate() - jan4Dow + (week - 1) * 7)
  return mon
}
function dayFromDate(dateStr: string | undefined, weekKey: string): number {
  if (!dateStr) return 1
  const mon = weekMonday(weekKey)
  const d = new Date(dateStr + "T00:00:00Z")
  if (!mon || isNaN(d.getTime())) return 1
  const diff = Math.round((d.getTime() - mon.getTime()) / 86400000)
  return Math.min(7, Math.max(1, diff + 1))
}

interface Row { description: string; amount: number; voceKey: string; day: number; auto: boolean }

export function ReceiptSortModal({ entries, cells, weekKey, merchant, total, onClose, onApply }: {
  entries: EntryRow[]
  cells: CellMeta[]
  weekKey: string
  merchant?: string
  total?: number
  onClose: () => void
  onApply: (assignments: { code: string; amount: number }[]) => Promise<void>
}) {
  const api = useApi()
  const { voci, vociList } = useMemo(() => {
    const voci = new Map<string, Voce>()
    for (const c of cells) {
      const key = (c.label && c.label.trim()) || `item-${c.item}`
      let v = voci.get(key)
      if (!v) { v = { key, label: (c.label && c.label.trim()) || `Voce ${c.item}`, byDay: {} }; voci.set(key, v) }
      if (c.day >= 1 && c.day <= 7) v.byDay[c.day] = c.code
    }
    const vociList = [...voci.values()].filter((v) => Object.keys(v.byDay).length > 0)
    return { voci, vociList }
  }, [cells])

  const defDay = dayFromDate(entries[0]?.date, weekKey)
  const [rows, setRows] = useState<Row[]>(() =>
    entries.map((e) => ({ description: e.description || e.istat_label || "—", amount: e.amount_eur, voceKey: "", day: defDay, auto: false })))
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Ask the LLM where each amount goes (meal out → one total on the right
  // SPESA TOTALE voce; groceries → per line). Fall back to the raw entries.
  useEffect(() => {
    let cancelled = false
    // Fast path: a single meal-out entry (ECOICOP 11.1.1) is mapped locally to
    // the right "SPESA TOTALE" voce — no second LLM round-trip.
    if (entries.length === 1 && entries[0].istat_category === "11.1.1") {
      const m = (merchant || "").toLowerCase()
      const isBar = /\bbar\b|caff|pasticc|gelat/.test(m) && !/ristorant|trattori|pizzeri|osteri/.test(m)
      const voce = vociList.find((v) => /spesa totale/i.test(v.label) && (isBar ? /bar|pasticc|gelat/i.test(v.label) : /ristorant|pizzeri|osteri|trattori/i.test(v.label)))
      if (voce) {
        setRows([{ description: merchant || entries[0].description || "Pasto fuori casa", amount: total ?? entries[0].amount_eur, voceKey: voce.key, day: defDay, auto: true }])
        setLoading(false)
        return
      }
    }
    const lines = entries.map((e) => ({ description: e.description || e.istat_label || "", amount: e.amount_eur }))
    suggestMapping(api, { merchant, total, lines, voci: vociList.map((v) => ({ id: v.key, label: v.label })) })
      .then((res) => {
        if (cancelled || !res.assignments?.length) return
        setRows(res.assignments.map((a) => ({
          description: a.note || "",
          amount: Number(a.amount) || 0,
          voceKey: voci.has(a.voce_id) ? a.voce_id : "",
          day: defDay,
          auto: true,
        })))
      })
      .catch(() => { /* keep heuristic rows */ })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [api]) // eslint-disable-line react-hooks/exhaustive-deps

  const cellFor = (r: Row) => voci.get(r.voceKey)?.byDay[r.day] ?? null
  const usable = rows.filter((r) => cellFor(r) && r.amount > 0)

  const apply = async () => {
    setBusy(true); setErr(null)
    try {
      const map = new Map<string, number>()
      for (const r of rows) {
        const code = cellFor(r)
        if (!code || !(r.amount > 0)) continue
        map.set(code, (map.get(code) ?? 0) + r.amount)
      }
      await onApply([...map.entries()].map(([code, amount]) => ({ code, amount })))
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e)); setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "grid", placeItems: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", background: "var(--bg)", color: "var(--text)", borderRadius: 12, border: "1px solid var(--border)", width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto", padding: 20 }}>
        <style>{"@keyframes rsm-spin{to{transform:rotate(360deg)}}"}</style>
        <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Smista scontrino nel diario</h2>
        <p style={{ margin: "0 0 14px", color: "var(--muted)", fontSize: 13 }}>
          {merchant ? <><strong>{merchant}</strong> · </> : null}Settimana <strong>{weekKey}</strong>
          {total != null ? <> · totale {total.toFixed(2)} €</> : null}. {loading
            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 13, height: 13, border: "2px solid var(--border)", borderTopColor: "var(--accent)", borderRadius: "50%", animation: "rsm-spin 0.8s linear infinite", display: "inline-block" }} />
                🤖 ottimizzo con l'AI… (intanto puoi già modificare)
              </span>
            : "🟢 = proposto dall'AI. Controlla voce e giorno, poi conferma."}
        </p>

        {vociList.length === 0 && (
          <p style={{ color: "#e0a000" }}>Questo modello non ha voci con etichetta. Apri l'editor celle e assegna le etichette per abilitare lo smistamento.</p>
        )}

        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 1.6fr 64px 64px 28px", gap: 8, fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
            <span>Voce scontrino</span><span>Importo</span><span>Va nel diario in</span><span>Giorno</span><span>Cella</span><span />
          </div>
          {rows.map((r, i) => {
            const code = cellFor(r)
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px 1.6fr 64px 64px 28px", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 13 }}>{r.auto && <span title="proposto dall'AI">🟢 </span>}{r.description || "—"}</span>
                <input type="number" step="0.01" value={r.amount}
                  onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, amount: Number(e.target.value) || 0 } : x))} style={inp} />
                <VoceCombo value={r.voceKey} options={vociList} onChange={(key) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, voceKey: key, auto: false } : x))} />
                <select value={r.day} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, day: Number(e.target.value) } : x))} style={inp}>
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => <option key={d} value={d}>G{d}</option>)}
                </select>
                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: code ? "var(--text)" : "#d12" }}>{code ?? "n/d"}</span>
                <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} title="Rimuovi riga"
                  style={{ height: 28, width: 28, borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)", color: "#d12", cursor: "pointer", fontSize: 15, lineHeight: 1 }}>×</button>
              </div>
            )
          })}
          <button onClick={() => setRows((rs) => [...rs, { description: "", amount: 0, voceKey: "", day: defDay, auto: false }])} style={{ ...inp, marginTop: 4, cursor: "pointer" }}>＋ riga</button>
        </div>

        {err && <p style={{ color: "#ff7a85", marginTop: 12 }}>{err}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={{ ...btn, background: "var(--surface)" }}>Annulla</button>
          <button onClick={apply} disabled={busy || usable.length === 0} style={{ ...btn, background: "var(--accent)", color: "var(--accent-fg)", borderColor: "transparent", opacity: busy || usable.length === 0 ? 0.6 : 1 }}>
            {busy ? "Inserisco…" : `Inserisci ${usable.length} importi`}
          </button>
        </div>
      </div>
    </div>
  )
}

// Searchable voce picker (the diary has hundreds of voci).
function VoceCombo({ value, options, onChange }: { value: string; options: Voce[]; onChange: (key: string) => void }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState("")
  const sel = options.find((o) => o.key === value)
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    const list = s ? options.filter((o) => o.label.toLowerCase().includes(s)) : options
    return list.slice(0, 40)
  }, [q, options])
  const optStyle: React.CSSProperties = { padding: "6px 8px", fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
  return (
    <div style={{ position: "relative" }}>
      <input
        value={open ? q : (sel?.label ?? "")}
        placeholder="— cerca voce —"
        onFocus={() => { setOpen(true); setQ("") }}
        onChange={(e) => setQ(e.target.value)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        style={{ ...inp, color: sel || open ? "var(--text)" : "var(--muted)" }}
      />
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 300, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, maxHeight: 280, overflow: "auto", boxShadow: "0 10px 28px rgba(0,0,0,0.35)" }}>
          <div onMouseDown={() => { onChange(""); setOpen(false) }} style={{ ...optStyle, color: "var(--muted)" }}>— ignora —</div>
          {filtered.map((o) => (
            <div key={o.key} onMouseDown={() => { onChange(o.key); setOpen(false) }} style={{ ...optStyle, background: o.key === value ? "var(--surface)" : undefined }}>{o.label}</div>
          ))}
          {filtered.length === 0 && <div style={{ ...optStyle, color: "var(--muted)", cursor: "default" }}>nessun risultato</div>}
        </div>
      )}
    </div>
  )
}

const inp: React.CSSProperties = { height: 30, padding: "0 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", fontSize: 13, width: "100%" }
const btn: React.CSSProperties = { height: 36, padding: "0 16px", borderRadius: 8, border: "1px solid var(--border)", color: "var(--text)", cursor: "pointer", fontWeight: 600, fontSize: 14 }
